import { useEffect, useState } from "react";

import {
  useReverseGeocodingQuery,
  useSearchPlaceQuery
} from "@/api/place/usePlaceAPI";
import { MESSAGE } from "@/constants/message";
import { COORDINATE_POTENTIAL_REGEX } from "@/constants/regex";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDebounce } from "@/hooks/useDebounce";
import { PlaceItem, ReverseGeocodingResponse } from "@/types/place";

import { twMerge } from "tailwind-merge";

import SearchAutocomplete from "../ui/searchAutocomplete";

const TEST_ID_PREFIX = "search-location-popup";

const SearchLocationPopup = () => {
  const { handleAddMarker, handleRemoveMarkerById } = useMap();

  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const debouncedKeyword = useDebounce(searchKeyword, 500);

  const { currentCenter } = useMap();

  const isSearchByCoordinate = COORDINATE_POTENTIAL_REGEX.test(searchKeyword);

  const { data: placeData, isLoading } = useSearchPlaceQuery(
    {
      query: debouncedKeyword,
      limit: 10,
      proximity: `${currentCenter[0]},${currentCenter[1]}`
    },
    {
      enabled: !!debouncedKeyword && !isSearchByCoordinate
    }
  );

  const [latitude, longitude] = debouncedKeyword
    .split(",")
    .map((s) => s.trim());

  const { data: placeByCoordinateData, isLoading: isPlaceByCoordinateLoading } =
    useReverseGeocodingQuery(
      { latitude, longitude },
      { enabled: isSearchByCoordinate }
    );

  const highlightKeyword = (text: string) => {
    if (!debouncedKeyword) return text;
    const regex = new RegExp(`(${debouncedKeyword})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, idx) =>
      regex.test(part) ? (
        <span key={idx} className="font-bold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const renderPlaceOption = (place: PlaceItem, _keyword: string) => {
    const { name, full_address, coordinates } = place;
    const title = `${name}, ${full_address}`;
    // Add keyword argument
    return (
      <div className="flex flex-col overflow-hidden py-1 hover:bg-teal-50">
        <p className="truncate text-txtHeading" title={title}>
          {highlightKeyword(title)}
        </p>
        <span className="text-[10px]">
          {coordinates.latitude}, {coordinates.longitude}
        </span>
      </div>
    );
  };

  const handleSelectPlace = (
    place: PlaceItem | ReverseGeocodingResponse["properties"] | null
  ) => {
    if (!place) return;
    const { coordinates } = place;
    handleAddMarker(
      "search-location",
      [coordinates.longitude, coordinates.latitude],
      place
    );
  };

  const getNodataText = (): string => {
    if (debouncedKeyword.length < 3 || isLoading || isPlaceByCoordinateLoading)
      return "";

    if (isSearchByCoordinate) {
      return "Coordinate not found. Check your input";
    }

    return MESSAGE.COULD_NOT_FIND_LOCATION;
  };

  useEffect(() => {
    return () => {
      handleRemoveMarkerById("search-location");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={twMerge(
        "absolute top-[30px] left-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[338px] max-h-[90vh]",
        "flex flex-col"
      )}
    >
      <div className="flex flex-col p-4 gap-8">
        <span className="text-xl font-semibold text-txtHeading mt-2">
          Search
        </span>
        <div className="flex flex-col">
          <SearchAutocomplete
            className={twMerge(
              "w-full",
              isSearchByCoordinate &&
                !placeByCoordinateData?.length &&
                "border-cinnabar focus-within:border-cinnabar"
            )}
            options={
              (isSearchByCoordinate
                ? placeByCoordinateData?.map((item) => item.properties)
                : placeData?.places) ?? []
            }
            getOptionLabel={(option) => option.name}
            getKey={(option) =>
              `${option.coordinates.latitude}-${option.coordinates.longitude}`
            }
            renderOption={renderPlaceOption}
            onSelect={handleSelectPlace}
            placeholder="Enter location or coordinates"
            isSearchApi
            setSearchKeyword={setSearchKeyword}
            noDataTxt={getNodataText()}
            noDataTxtClassName={twMerge(
              "text-xs",
              isSearchByCoordinate ? "text-cinnabar" : "text-txtHeading"
            )}
            showNoDataAsOption={!isSearchByCoordinate}
            testId={TEST_ID_PREFIX}
          />
        </div>
      </div>
    </div>
  );
};

export default SearchLocationPopup;
